import { useId, useMemo, useState } from "react";
import {
  useResolveOpenVariable,
  type OpenVariable,
  type Resolution,
} from "../api";

import styles from "./OpenVariableCard.module.css";

interface Props {
  projectId: string;
  sourceRunId: string;
  variable: OpenVariable;
  resolution: Resolution | undefined;
  onResolved?: (newRunId: string) => void;
}

// One interactive open-variables card. Renders the variable + its
// blocking action + owner, plus either:
//   - A "Resolved" panel showing the operator's saved resolution
//     text + author + timestamp (read-only for v1).
//   - A textarea + Save button driving the resolve mutation.
//
// On save the mutation hits POST /open-variables/resolve which
// persists the resolution AND triggers an incremental Atlas re-run
// server-side. We surface the new run id back to the parent so it
// can scroll/highlight the freshly-produced run.
export const OpenVariableCard = ({
  projectId,
  sourceRunId,
  variable,
  resolution,
  onResolved,
}: Props) => {
  const [draft, setDraft] = useState("");
  const mutation = useResolveOpenVariable();
  // React-generated stable id; avoids collisions when two variables
  // on the same page have names that differ only in punctuation
  // (the previous id={`res-${variable.name}`} pattern was unsafe
  // because Atlas variable names are free text).
  const textareaId = useId();

  const isResolved = Boolean(resolution);
  const submitDisabled = useMemo(
    () => draft.trim().length === 0 || mutation.isLoading,
    [draft, mutation.isLoading],
  );

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    try {
      const resp = await mutation.mutateAsync({
        project_id: projectId,
        variable_name: variable.name,
        variable_description: variable.description,
        resolution_text: trimmed,
        source_run_id: sourceRunId,
      });
      setDraft("");
      onResolved?.(resp.new_run.run_id);
    } catch (err) {
      // The mutation surfaces error state via mutation.isError /
      // mutation.error - no console noise here. Operator sees the
      // error inline below the textarea.
      void err;
    }
  };

  return (
    <li className={`${styles.item} ${isResolved ? styles.itemResolved : ""}`}>
      <div className={styles.head}>
        <div className={styles.name}>{variable.name}</div>
        {isResolved && (
          <span className={styles.resolvedPill}>Resolved</span>
        )}
      </div>
      <p className={styles.desc}>{variable.description}</p>
      <div className={styles.action}>
        <span className={styles.actionLabel}>Blocking action</span>
        <span>{variable.blocking_action}</span>
      </div>
      {variable.owner && (
        <div className={styles.owner}>
          Owner: <strong>{variable.owner}</strong>
        </div>
      )}

      {isResolved && resolution ? (
        <div className={styles.resolutionBox}>
          <div className={styles.resolutionLabel}>Operator resolution</div>
          <p className={styles.resolutionText}>
            {resolution.resolution_text}
          </p>
          <div className={styles.resolutionMeta}>
            <span>{resolution.created_by ?? "unknown"}</span>
            <span className={styles.dot}>·</span>
            <span>{new Date(resolution.created_at).toLocaleString()}</span>
          </div>
        </div>
      ) : (
        <div className={styles.editor}>
          <label className={styles.editorLabel} htmlFor={textareaId}>
            Type the resolution and Atlas will re-read this project state
          </label>
          <textarea
            id={textareaId}
            className={styles.textarea}
            placeholder="e.g. Confirmed CoE at £42,150 via SSEN email 2026-05-10."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={mutation.isLoading}
            rows={3}
          />
          <div className={styles.editorBar}>
            <span className={styles.editorHint}>
              Atlas reasons over its prior decision + your resolution +
              matching answers from other projects. Source documents are
              not re-read.
            </span>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={submit}
              disabled={submitDisabled}
            >
              {mutation.isLoading ? "Atlas re-running…" : "Save + re-run"}
            </button>
          </div>
          {mutation.isError && (
            <div className={styles.err}>
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Save failed. Try again."}
            </div>
          )}
        </div>
      )}
    </li>
  );
};
